import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/App";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Filter, X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Tienda() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get("brand") || "");
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [onlyFeatured, setOnlyFeatured] = useState(searchParams.get("featured") === "true");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [sortBy, setSortBy] = useState("default");
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  useEffect(() => {
    const category = searchParams.get("category");
    const search = searchParams.get("search");
    const featured = searchParams.get("featured");
    
    if (category) setSelectedCategory(category);
    if (search) setSearchQuery(search);
    if (featured === "true") setOnlyFeatured(true);
    
    fetchProducts();
  }, [searchParams, page]);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get("/brands");
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.set("category", selectedCategory);
      if (searchQuery) params.set("search", searchQuery);
      if (selectedBrand) params.set("brand", selectedBrand);
      if (priceRange[0] > 0) params.set("min_price", priceRange[0].toString());
      if (priceRange[1] < 1000) params.set("max_price", priceRange[1].toString());
      if (onlyFeatured) params.set("featured", "true");
      params.set("page", page.toString());
      params.set("limit", "20");

      const response = await api.get(`/products?${params.toString()}`);
      setProducts(response.data.products);
      setPagination({
        total: response.data.total,
        page: response.data.page,
        pages: response.data.pages,
      });
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    const params = new URLSearchParams();
    if (selectedCategory) params.set("category", selectedCategory);
    if (searchQuery) params.set("search", searchQuery);
    if (selectedBrand) params.set("brand", selectedBrand);
    if (onlyFeatured) params.set("featured", "true");
    setSearchParams(params);
    setFiltersOpen(false);
  };

  const handleClearFilters = () => {
    setSelectedCategory("");
    setSelectedBrand("");
    setPriceRange([0, 1000]);
    setOnlyFeatured(false);
    setSearchQuery("");
    setPage(1);
    setSearchParams({});
  };

  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case "price-asc":
        return sorted.sort((a, b) => a.price - b.price);
      case "price-desc":
        return sorted.sort((a, b) => b.price - a.price);
      case "name-asc":
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case "name-desc":
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      default:
        return sorted;
    }
  }, [products, sortBy]);

  const activeFiltersCount = [
    selectedCategory,
    selectedBrand,
    onlyFeatured,
    priceRange[0] > 0 || priceRange[1] < 1000,
  ].filter(Boolean).length;

  const getCategoryName = (id) => categories.find(c => c.category_id === id)?.name || "";

  const FiltersContent = () => (
    <div className="space-y-6">
      {/* Categories */}
      <div>
        <Label className="text-base font-heading uppercase tracking-wide mb-3 block">
          Categoría
        </Label>
        <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="category-filter">
            <SelectValue placeholder="Todas las categorías" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.category_id} value={cat.category_id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Brands */}
      <div>
        <Label className="text-base font-heading uppercase tracking-wide mb-3 block">
          Marca
        </Label>
        <Select value={selectedBrand || "all"} onValueChange={(v) => setSelectedBrand(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="brand-filter">
            <SelectValue placeholder="Todas las marcas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las marcas</SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand} value={brand}>
                {brand}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div>
        <Label className="text-base font-heading uppercase tracking-wide mb-3 block">
          Precio (USD)
        </Label>
        <div className="px-2">
          <Slider
            value={priceRange}
            onValueChange={setPriceRange}
            max={1000}
            step={10}
            className="mb-4"
          />
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>${priceRange[0]}</span>
            <span>${priceRange[1]}+</span>
          </div>
        </div>
      </div>

      {/* Featured only */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="featured"
          checked={onlyFeatured}
          onCheckedChange={setOnlyFeatured}
          data-testid="featured-filter"
        />
        <Label htmlFor="featured" className="cursor-pointer">
          Solo productos destacados
        </Label>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4">
        <Button
          variant="outline"
          onClick={handleClearFilters}
          className="flex-1"
        >
          Limpiar
        </Button>
        <Button
          onClick={handleApplyFilters}
          className="flex-1 bg-primary hover:bg-primary/90"
          data-testid="apply-filters-btn"
        >
          Aplicar
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="tienda-page">
      {/* Header */}
      <div className="bg-primary text-white py-8 relative">
        <div className="relative industrial-texture"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl">
            {searchQuery
              ? `Resultados para "${searchQuery}"`
              : selectedCategory
              ? getCategoryName(selectedCategory)
              : "TODOS LOS PRODUCTOS"}
          </h1>
          <p className="text-white/80 mt-2">
            {pagination.total} producto{pagination.total !== 1 ? "s" : ""} encontrado{pagination.total !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28 bg-white p-6 rounded-sm border">
              <h2 className="font-heading text-lg mb-6 uppercase tracking-wide">Filtros</h2>
              <FiltersContent />
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4 mb-6">
              {/* Mobile filter button */}
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="outline" className="gap-2" data-testid="mobile-filter-btn">
                    <Filter className="w-4 h-4" />
                    Filtros
                    {activeFiltersCount > 0 && (
                      <span className="bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="font-heading uppercase tracking-wide">
                      Filtros
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FiltersContent />
                  </div>
                </SheetContent>
              </Sheet>

              {/* Active filters */}
              <div className="hidden md:flex items-center gap-2 flex-1 overflow-x-auto">
                {selectedCategory && (
                  <span className="flex items-center gap-1 bg-primary/10 text-primary text-sm px-3 py-1 rounded-full">
                    {getCategoryName(selectedCategory)}
                    <button onClick={() => { setSelectedCategory(""); handleApplyFilters(); }}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedBrand && (
                  <span className="flex items-center gap-1 bg-primary/10 text-primary text-sm px-3 py-1 rounded-full">
                    {selectedBrand}
                    <button onClick={() => { setSelectedBrand(""); handleApplyFilters(); }}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48" data-testid="sort-select">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Relevancia</SelectItem>
                  <SelectItem value="price-asc">Menor precio</SelectItem>
                  <SelectItem value="price-desc">Mayor precio</SelectItem>
                  <SelectItem value="name-asc">A-Z</SelectItem>
                  <SelectItem value="name-desc">Z-A</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Products grid */}
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-muted rounded-sm aspect-[3/4] skeleton-pulse"></div>
                ))}
              </div>
            ) : sortedProducts.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sortedProducts.map((product) => (
                    <ProductCard key={product.product_id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-4">
                      Página {page} de {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={page === pagination.pages}
                      onClick={() => setPage(page + 1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">No se encontraron productos</p>
                <Button variant="link" onClick={handleClearFilters} className="mt-2">
                  Limpiar filtros
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
